import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import ProjectDetail from "@/pages/ProjectDetail";
import FreeAudit from "@/pages/FreeAudit";
import VsCompetitor from "@/pages/VsCompetitor";
import SocialPage from "@/pages/SocialPage";
import SocialCalendarPage from "@/pages/SocialCalendarPage";
import Pricing from "@/pages/Pricing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import AdminMetrics from "@/pages/AdminMetrics";
import AdminConsole from "@/pages/AdminConsole";
import Settings from "@/pages/Settings";
import StatusPage from "@/pages/StatusPage";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Docs from "@/pages/Docs";
import DocArticle from "@/pages/DocArticle";
import DocsChangelog from "@/pages/DocsChangelog";
import QuickStart from "@/pages/QuickStart";
import ScrollToTop from "@/components/app/ScrollToTop";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen grid place-items-center font-display text-sm tracking-tight">
        Loading workspace…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user && user !== null) return <Navigate to="/app" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/free-audit" element={<FreeAudit />} />
          <Route path="/social" element={<SocialPage />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/vs/:competitor" element={<VsCompetitor />} />
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
          <Route path="/app" element={<Protected><Dashboard /></Protected>} />
          <Route path="/app/settings" element={<Protected><Settings /></Protected>} />
          <Route path="/app/calendar" element={<Protected><SocialCalendarPage /></Protected>} />
          <Route path="/app/projects/:projectId" element={<Protected><ProjectDetail /></Protected>} />
          <Route path="/admin" element={<Protected><AdminConsole /></Protected>} />
          <Route path="/admin/metrics" element={<Protected><AdminMetrics /></Protected>} />
          <Route path="/status" element={<StatusPage />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/docs" element={<Docs />} />
          <Route path="/docs/changelog" element={<DocsChangelog />} />
          <Route path="/docs/:slug" element={<DocArticle />} />
          <Route path="/quick-start" element={<QuickStart />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </AuthProvider>
  );
}
